import React from 'react';
import {makeStyles} from 'tss-react/mui';
import {FormattedMessage, useIntl} from 'react-intl';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {useDocumentCollection} from '../context/DocumentCollectionContext';
import {SCREENS} from '../constants/documentCollection';

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: '24px 16px',
    maxWidth: 600,
    margin: '0 auto',
    '@media (min-width: 768px)': {
      padding: '40px 24px',
    },
  },
  greeting: {
    fontSize: theme.typography.pxToRem(22),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    textAlign: 'center',
    direction: 'ltr',
    marginBottom: 20,
    '@media (min-width: 768px)': {
      fontSize: theme.typography.pxToRem(26),
    },
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 24,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    fontSize: theme.typography.pxToRem(16),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  infoIcon: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(20),
  },
  infoLabel: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    marginBottom: 2,
  },
  infoValue: {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(16),
  },
  infoBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  welcomeText: {
    fontSize: theme.typography.pxToRem(16),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    textAlign: 'center',
    lineHeight: 1.7,
    marginBottom: 24,
    padding: '0 10px',
    direction: 'ltr',
  },
  warningBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '14px 16px',
    backgroundColor: theme.palette.error.light,
    border: `1px solid ${theme.palette.warning.main}`,
    borderRadius: 10,
    marginBottom: 32,
    textAlign: 'left',
    direction: 'ltr',
  },
  warningIcon: {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(22),
    marginTop: 2,
    flexShrink: 0,
  },
  warningText: {
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    lineHeight: 1.6,
  },
  warningBold: {
    fontWeight: 700,
  },
  ctaButton: {
    display: 'block',
    width: '100%',
    maxWidth: 400,
    margin: '0 auto',
    padding: '16px 32px',
    borderRadius: 30,
    border: 'none',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const WelcomeScreen = () => {
  const {classes} = useStyles();
  const intl = useIntl();
  const {customerName, policyNumber, licensePlate, token, setScreen} = useDocumentCollection();

  return (
    <div className={classes.container}>
      <div className={classes.greeting}>
        {intl.formatMessage({id: 'docCollection.welcome.greeting'}, {customerName})}
      </div>

      <div className={classes.infoRow}>
        <div className={classes.infoItem}>
          <ContentCopyOutlinedIcon className={classes.infoIcon} />
          <div className={classes.infoBlock}>
            <span className={classes.infoLabel}>
              <FormattedMessage id='docCollection.welcome.policyLabel' />
            </span>
            <span className={classes.infoValue}>{policyNumber}</span>
          </div>
        </div>
        <div className={classes.infoItem}>
          <DirectionsCarOutlinedIcon className={classes.infoIcon} />
          <div className={classes.infoBlock}>
            <span className={classes.infoLabel}>
              <FormattedMessage id='docCollection.welcome.vehicleLabel' />
            </span>
            <span className={classes.infoValue}>{licensePlate}</span>
          </div>
        </div>
      </div>

      <div className={classes.welcomeText}>
        <FormattedMessage id='docCollection.welcome.text' values={{br: <br />}} />
      </div>

      <div className={classes.warningBox}>
        <WarningAmberIcon className={classes.warningIcon} />
        <div className={classes.warningText}>
          <span className={classes.warningBold}>
            <FormattedMessage id='docCollection.welcome.warningTitle' />
          </span>
          <FormattedMessage id='docCollection.welcome.warningText' />
        </div>
      </div>

      <button
        type='button'
        className={classes.ctaButton}
        onClick={() => {
          sessionStorage.setItem(`doc_welcome_seen_${token}`, 'true');
          setScreen(SCREENS.UPLOAD);
        }}
      >
        <FormattedMessage id='docCollection.welcome.cta' />
      </button>
    </div>
  );
};

export default WelcomeScreen;
